import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { ReactComponent as BlueCalendar } from '../../images/icons/blue_calendar.svg';

function FieldWrapper({ label, children }) {
  return (
    <div className="client-input-field">
      <span className="client-input-label">{label}</span>
      {children}
    </div>
  );
}

FieldWrapper.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

export function EditTextField({
  label,
  value,
  onChange,
  placeholder,
}) {
  return (
    <FieldWrapper label={label}>
      <input
        className="client-simple-input"
        type="text"
        value={value ?? ''}
        onChange={onChange}
        placeholder={placeholder}
      />
    </FieldWrapper>
  );
}

EditTextField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

export function EditSelectField({
  label,
  value,
  onChange,
  options,
}) {
  return (
    <FieldWrapper label={label}>
      <select
        className="client-simple-input"
        value={value || ''}
        onChange={onChange}
      >
        <option value="" disabled hidden>Select</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </FieldWrapper>
  );
}

EditSelectField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export function EditDateField({
  label,
  value,
  onChange,
}) {
  return (
    <FieldWrapper label={label}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DatePicker
          className="client-date-picker"
          inputFormat="MM/DD/YYYY"
          value={value ? moment(value, 'MM/DD/YYYY') : null}
          onChange={(dateValue) => {
            onChange(dateValue ? dateValue.format('MM/DD/YYYY') : '');
          }}
          slots={{ openPickerIcon: BlueCalendar }}
          slotProps={{
            textField: {
              placeholder: 'MM/DD/YYYY',
              size: 'small',
            },
            openPickerButton: {
              className: 'client-date-picker-btn',
            },
          }}
        />
      </LocalizationProvider>
    </FieldWrapper>
  );
}

EditDateField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export function EditYesNoField({
  label,
  value,
  onChange,
}) {
  return (
    <FieldWrapper label={label}>
      <RadioGroup
        row
        className="client-input-radio-group"
        value={value === null || value === undefined ? '' : String(value)}
        onChange={(e) => {
          if (e.target.value === 'true') onChange(true);
          else if (e.target.value === 'false') onChange(false);
        }}
      >
        <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
        <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
      </RadioGroup>
    </FieldWrapper>
  );
}

EditYesNoField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

export function EditRadioField({
  label,
  value,
  onChange,
  options,
}) {
  return (
    <FieldWrapper label={label}>
      <RadioGroup
        row
        className="client-input-radio-group"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option}
            value={option}
            control={<Radio size="small" />}
            label={option}
          />
        ))}
      </RadioGroup>
    </FieldWrapper>
  );
}

EditRadioField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export function EditIndustryField({
  label,
  value,
  onChange,
  options,
}) {
  const groupedOptions = useMemo(() => {
    const groups = {};
    options.forEach((option) => {
      const category = option.category || 'Other';
      if (!groups[category]) groups[category] = [];
      groups[category].push(option);
    });
    return groups;
  }, [options]);

  return (
    <FieldWrapper label={label}>
      <select
        className="client-simple-input"
        value={value?.title || ''}
        onChange={(e) => {
          const selectedOption = options.find((option) => option.title === e.target.value);
          onChange(selectedOption || null);
        }}
      >
        <option value="" disabled hidden>Select</option>
        {Object.entries(groupedOptions).map(([category, categoryOptions]) => (
          <optgroup key={category} label={category}>
            {categoryOptions.map((option) => (
              <option key={option.title} value={option.title}>
                {option.title}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </FieldWrapper>
  );
}

EditIndustryField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
};
